import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { calcSawFields } from "@/pages/workOrders/config/assetFieldConfig";
import { OverviewHeader } from "../../weldlines/models/types";
import {
	mapBreak,
	RawBreak,
} from "../../weldlines/repositories/overviewRepository";
import {
	SawEntryProduct,
	SawFinishBundle,
	SawLiveSignals,
	SawNextProduct,
	SawOutgoingBundle,
	SawOverviewData,
	SawProductRun,
	SawRunningProduct,
	SawRunProgress,
	SawScheduleBudget,
	SawShiftProduction,
} from "../models/types";
import { formatEntryLength } from "../utils/entryBarcode";

const moment = getMomentTz();

// SQL Server returns datetimes without timezone suffix → parse as UTC.
const parseUtc = (s: string | null | undefined): Date | null =>
	s ? moment.utc(s).toDate() : null;

type RawHeader = Omit<
	OverviewHeader,
	| "ServerTime"
	| "CurrShiftStart"
	| "CurrShiftEnd"
	| "PrevShiftStart"
	| "PrevShiftEnd"
> & {
	ServerTime: string;
	CurrShiftStart: string | null;
	CurrShiftEnd: string | null;
	PrevShiftStart: string | null;
	PrevShiftEnd: string | null;
};

const mapHeader = (r: RawHeader): OverviewHeader => ({
	AssetId: r.AssetId,
	AssetName: r.AssetName,
	ServerTime: moment.utc(r.ServerTime).toDate(),
	CurrShiftId: r.CurrShiftId,
	CurrShiftStart: parseUtc(r.CurrShiftStart),
	CurrShiftEnd: parseUtc(r.CurrShiftEnd),
	CurrShiftLabel: r.CurrShiftLabel,
	CurrCrew: r.CurrCrew,
	PrevShiftId: r.PrevShiftId,
	PrevShiftStart: parseUtc(r.PrevShiftStart),
	PrevShiftEnd: parseUtc(r.PrevShiftEnd),
	PrevShiftLabel: r.PrevShiftLabel,
	PrevCrew: r.PrevCrew,
});

// The reset sentinel for TotalFedLast between cuts is ≈ −0.12. Anything below
// half an inch isn't a real cut — surface as null so the lane renders "—".
const cleanLastCut = (v: number | null): number | null =>
	v == null || v < 0.5 ? null : v;

const projectRunningProduct = (
	live: SawLiveSignals,
	curr: SawProductRun | null,
): SawRunningProduct => ({
	// Identity from the Running work order; lengths/speeds stay PLC-sourced.
	productNumber: curr?.PartNumber ?? null,
	extendedPartNumber: curr?.ExtendedPartNumber ?? null,
	workOrder: curr?.WorkOrderNumber ?? null,
	// LIVE
	targetBeamLength: live.CutLenTarget,
	maxLength: live.LengthMax,
	minLength: live.LengthMin,
	lengthOfLastCut: cleanLastCut(live.LastCutLen),
	bladeSpeedSchdFpm: live.BladeSpeedSchdFpm,
	bladeSpeedCutFpm: live.BladeSpeedCutFpm,
	// Cuts per mill bundle from the current WO's SawSpecs; null without a WO.
	// Layer[0] = layers per bundle, not cuts.
	cutsPerBundle: curr
		? (calcSawFields(curr, curr.Quantity)?.cutsPerBundle ?? null)
		: null,
});

const projectOutgoingBundle = (live: SawLiveSignals): SawOutgoingBundle => ({
	// Not surfaced on the card; ExitingBundleId feeds the reclassify/QC forms.
	exitingBundleId: null,
	stack: null,
	// LIVE — PLC-authoritative via MathInt[2] with derived fallback (SP COALESCE).
	totalPiecesPerBundle: live.PiecesPerBundle,
});

const projectCurrentShift = (
	live: SawLiveSignals,
	curr: SawProductRun | null,
): SawShiftProduction => ({
	finishBundlesCutPerShift: live.BundlesThisShift,
	finishedBundlesPerHr: live.BundlesPerHourShift,
	finishBundlesCutPerProductRun: curr?.BundlesCompleted ?? null,
});

// Previous shift is null until the job has actually filled the Prev* columns
// at least once (only happens after a real shift rollover). Treating "all
// nulls" as "no prev yet" keeps the card in placeholder mode instead of
// showing zeros that could be confused with "the prev shift produced zero".
const projectPreviousShift = (
	live: SawLiveSignals,
): SawShiftProduction | null => {
	if (live.PrevBundlesShift == null && live.PrevCutsShift == null) {
		return null;
	}
	return {
		finishBundlesCutPerShift: live.PrevBundlesShift,
		finishedBundlesPerHr: live.PrevBundlesPerHourShift,
		finishBundlesCutPerProductRun: null, // PENDING (needs WO run boundaries)
	};
};

const projectScheduleBudget = (live: SawLiveSignals): SawScheduleBudget => ({
	// Bud / Scheduled targets stay PENDING (MES budget config not wired).
	bundlesPerHrSchd: null,
	bundlesPerHrBudget: null,
	bundlesPerShiftSchd: null,
	bundlesPerShiftBudget: null,
	avgCycleTimePerCut: live.AvgCycleSecPerCut,
	cycleTimePerLastCut: live.LastCycleSec,
});

// RS4 roles: a role without a work order arrives with WorkOrderId null →
// slice null → the card renders all "—".
const projectNextProduct = (
	next: SawProductRun | null,
): SawNextProduct | null => {
	if (next?.WorkOrderId == null) return null;
	return {
		partNumber: next.PartNumber,
		workOrder: next.WorkOrderNumber,
		extendedPartNumber: next.ExtendedPartNumber,
		section: next.SectionName,
		grade: next.GradeName,
		pieces: next.Quantity,
		bundleLength: next.MillLength != null ? `${next.MillLength} ft` : null,
		// Mill bundles the operator has to load to cover the order (numMLB).
		bundlesToLoad: calcSawFields(next, next.Quantity)?.numMLB ?? null,
	};
};

const projectFinishBundle = (
	curr: SawProductRun | null,
): SawFinishBundle | null => {
	if (curr?.WorkOrderId == null) return null;
	return {
		customer: curr.CustomerName,
		customerPartNumber: curr.CustomerPartNumber,
		strapLength: curr.BandLength != null ? String(curr.BandLength) : null,
		groovedTimberLength: curr.GroovedTimber,
	};
};

const projectRunProgress = (
	curr: SawProductRun | null,
): SawRunProgress | null => {
	if (curr?.WorkOrderId == null) return null;
	return {
		workOrder: curr.WorkOrderNumber,
		bundlesCut: curr.BundlesCompleted,
		bundlesTarget:
			curr.Quantity != null && curr.MillPcB
				? Math.ceil(curr.Quantity / curr.MillPcB)
				: null,
		piecesCut: curr.PiecesCut,
		quantity: curr.Quantity,
		notes: curr.Notes,
		nextLocations: curr.NextLocations,
	};
};

// RS3: latest entry-bundle scan of the current shift.
interface RawEntryScan {
	EntryBundleId: number;
	TagNo: string | null;
	SectionName: string | null;
	GradeName: string | null;
	HeatNo: string | null;
	MillLengthIn: number | null;
	Pieces: number | null;
	ProductId: number | null;
	PartNumber: string | null;
	ExtendedPartNumber: string | null;
	ScannedPartNumber: string | null;
	ScannedAt: string | null;
}

const projectEntryProduct = (r: RawEntryScan): SawEntryProduct => ({
	section: r.SectionName,
	grade: r.GradeName,
	pieces: r.Pieces,
	bundleLength: formatEntryLength(r.MillLengthIn, r.ScannedPartNumber != null),
	bundleId: r.TagNo,
	heatNo: r.HeatNo,
	productId: r.ProductId,
	partNumber: r.PartNumber,
	extendedPartNumber: r.ExtendedPartNumber,
	scannedAt: r.ScannedAt ? moment.utc(r.ScannedAt).toISOString() : null,
});

export const getSawOverview = async (
	assetId: number | null = null,
): Promise<SawOverviewData> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "AssetId", value: assetId }];

	const resp = await apiService.callV2("[FAB].[GetSawOverview]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching saw overview");
	}

	const headerRows = get(resp, "data.tables[0].rows", []) as RawHeader[];
	const liveRows = get(resp, "data.tables[1].rows", []) as SawLiveSignals[];
	const entryRows = get(resp, "data.tables[2].rows", []) as RawEntryScan[];
	const runRows = get(resp, "data.tables[3].rows", []) as SawProductRun[];
	const breakRows = get(resp, "data.tables[4].rows", []) as RawBreak[];

	const header = headerRows[0] ? mapHeader(headerRows[0]) : null;
	const live = liveRows[0] ?? null;
	const currRun = runRows.find((r) => r.Role === "Current") ?? null;
	const nextRun = runRows.find((r) => r.Role === "Next") ?? null;

	return {
		header,
		live,
		runningProduct: live ? projectRunningProduct(live, currRun) : null,
		outgoingBundle: live ? projectOutgoingBundle(live) : null,
		currentShift: live ? projectCurrentShift(live, currRun) : null,
		previousShift: live ? projectPreviousShift(live) : null,
		scheduleBudget: live ? projectScheduleBudget(live) : null,
		entryProduct: entryRows[0] ? projectEntryProduct(entryRows[0]) : null,
		nextProduct: projectNextProduct(nextRun),
		finishBundle: projectFinishBundle(currRun),
		runProgress: projectRunProgress(currRun),
		breaks: breakRows.map(mapBreak),
		// PENDING — kept null so the chart stays in placeholder mode.
		bundlesVsHours: null,
	};
};
